import type { ReactNode, RefObject } from "react";
import { useRef } from "react";
import { Transition as BaseTransition } from "react-transition-group";
import type {
  EnterHandler,
  ExitHandler,
} from "react-transition-group/Transition";

/**
 * most of the typings for our custom transition component has been pulled
 * directly from the react-transition-group/Transition component.
 *
 * However, only the required props (those that we are interested in) have been
 * extracted as well as custom child render method
 */
export type RefTransitionProps = {
  /**
   * children should be constructed as a function that takes the transition-ref
   * as its only parameter. This ref should be applied to the element which is
   * to be transitioned on changes.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children(ref: RefObject<any>): React.ReactNode | React.ReactNode[];

  /**
   * The duration of the transition, in milliseconds. Required unless
   * addEndListener is provided.
   *
   * You may specify a single timeout for all transitions:
   * ```js
   * timeout={500}
   * ```
   * or individually:
   * ```js
   * timeout={{
   * appear: 500,
   * enter: 300,
   * exit: 500,
   * }}
   * ```
   * - appear defaults to the value of `enter`
   * - enter defaults to `0`
   * - exit defaults to `0`
   */
  timeout:
    | number
    | {
        appear?: number | undefined;
        enter?: number | undefined;
        exit?: number | undefined;
      };

  /**
   * Show the component; triggers the enter or exit states
   */
  in?: boolean | undefined;

  /**
   * By default the child component is mounted immediately along with the
   * parent Transition component. If you want to "lazy mount" the component
   * on the first `in={true}` you can set `mountOnEnter`. After the first
   * enter transition the component will stay mounted, even on "exited",
   * unless you also specify `unmountOnExit`.
   */
  mountOnEnter?: boolean | undefined;

  /**
   * By default the child component stays mounted after it reaches the
   * 'exited' state. Set `unmountOnExit` if you'd prefer to unmount the
   * component after it finishes exiting.
   */
  unmountOnExit?: boolean | undefined;

  /**
   * it is possible to provide an explicit nodeRef if the integration needs
   * access to the ref that will be set by the transition component.
   *
   * @example
   * function MyComp() {
   *    const containerRef = useRef(); // will contain a reference to the div-element
   *
   *    return (
   *      <Transition nodeRef={containerRef}>
   *        {(ref) => <div ref={ref} /}
   *      </Transition>
   *    )
   * }
   */
  nodeRef?: RefObject<HTMLElement | null>;
};

export type TransitionCallbackProps = {
  onEnter: EnterHandler<HTMLElement>;
  onEntering: EnterHandler<HTMLElement>;
  onEntered: EnterHandler<HTMLElement>;

  onExit: ExitHandler<HTMLElement>;
  onExiting: ExitHandler<HTMLElement>;
  onExited: ExitHandler<HTMLElement>;
};

/**
 * custom Transtion component for react-transition-group similar to the
 * CSSTransition, however with a slightly altered API to improve DX.
 *
 * After the introduction of ReactStrictMode react-transition-group transitions
 * needs to be provided with a nodeRef prop to not use findDOMNode() which may
 * have a negative impact on performance.
 *
 * However, manually passing refs may become cumbersome to provide. Thus, this
 * transition aims to easy the API required to make Transitions for
 * react-transition-groups by provided all props required for children as
 * arguments.
 */
export function RefTransition(
  transitionProps: RefTransitionProps & Partial<TransitionCallbackProps>,
): ReactNode {
  const internalRef = useRef<HTMLElement>(null);
  const nodeRef = transitionProps.nodeRef ?? internalRef;

  return (
    <BaseTransition {...transitionProps} nodeRef={nodeRef}>
      {transitionProps.children(nodeRef)}
    </BaseTransition>
  );
}
